import * as Sentry from "@sentry/browser";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Badge,
  Button,
  DatePicker,
  Input,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import { ColumnsType } from "antd/lib/table/Table";
import { AxiosResponse } from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { GET_TAGS } from "../gql/general";
import { MANUAL_TAG_NAMES } from "../helpers/constants";
import { SelectOptionsInterface, TagRecord } from "../helpers/shared-interfaces";
import { isAutomaticTag, request } from "../helpers/utilities";
import FileUpload from "./FileUpload";
import ModalCreator from "./ModalCreator";

const { Option } = Select;

const tagColors: Record<string, string> = {
  Active: "green",
  Inactive: "volcano",
  Closed: "red",
  "Non-Paying": "orange",
  Deceased: "black",
  "No Comm.": "purple",
  Suspended: "magenta",
  Retired: "gold",
  "Retired with Redeemable Balance": "cyan",
  "Retired without Redeemable Balance": "geekblue",
  "Awaiting First Payment": "blue",
  Kindergarten: "lime",
  Dormant: "gray",
  "Ported Out": "brown",
};

interface TagScope {
  forCompany?: boolean;
  forCustomer?: boolean;
  forAccount?: boolean;
  forProduct?: boolean;
}

export interface AssignTagsOptions {
  effectiveDate?: string;
  notes?: string;
  proofOfClosureDocumentUrls?: string[];
  proofOfReopeningDocumentUrls?: string[];
}

interface Props {
  tagRecords: TagRecord[];
  tagScope: TagScope;
  /**
   * Called with the full new set of active tag IDs whenever tags change.
   * effectiveDate and notes are passed for manual tags when provided.
   * proofOfClosureDocumentUrls is passed when closing (Closed tag).
   * proofOfReopeningDocumentUrls is passed when removing the Closed tag.
   */
  onAssignTags: (
    tagIds: string[],
    options?: AssignTagsOptions,
  ) => Promise<void>;
  /**
   * Show the effective date column and the edit button for manual tags.
   * Enable for companies; disable for customers.
   */
  supportsEffectiveDate?: boolean;
  /**
   * Company id, required to enable the proof-of-closure document upload when
   * assigning the Closed tag.
   */
  companyId?: string;
  isSubmitting?: boolean;
  isReadOnly?: boolean;
}

const TagsManagement: React.FC<Props> = ({
  tagRecords,
  tagScope,
  onAssignTags,
  supportsEffectiveDate = false,
  companyId,
  isSubmitting = false,
  isReadOnly = false,
}) => {
  const [availableTags, setAvailableTags] = useState<SelectOptionsInterface[]>([]);
  const [isFetchingTags, setIsFetchingTags] = useState(false);
  const [localSubmitting, setLocalSubmitting] = useState(false);

  // Add modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addTagId, setAddTagId] = useState<string | undefined>();
  const [addEffectiveDate, setAddEffectiveDate] = useState<moment.Moment | null>(null);
  const [addNotes, setAddNotes] = useState("");
  const [addProofUrls, setAddProofUrls] = useState<string[]>([]);

  // Edit effective date modal (manual tags, supportsEffectiveDate only)
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState<TagRecord | null>(null);
  const [editEffectiveDate, setEditEffectiveDate] = useState<moment.Moment | null>(null);

  // Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingRecord, setDeletingRecord] = useState<TagRecord | null>(null);
  const [deleteEffectiveDate, setDeleteEffectiveDate] =
    useState<moment.Moment | null>(null);
  const [reopenProofUrls, setReopenProofUrls] = useState<string[]>([]);
  const [deleteComment, setDeleteComment] = useState("");

  // FileUpload owns its file list and exposes no reset, so it is keyed off this
  // and rebuilt on every modal visit.
  const [uploadSession, setUploadSession] = useState(0);

  const submitting = isSubmitting || localSubmitting;

  useEffect(() => {
    if (!isReadOnly) fetchAvailableTags();
  }, []);

  const fetchAvailableTags = async () => {
    setIsFetchingTags(true);
    try {
      const {
        data: { data },
      }: AxiosResponse<{ data: { tags: SelectOptionsInterface[] } }> = await request({
        query: GET_TAGS,
        variables: tagScope as Record<string, unknown>,
      });
      setAvailableTags(data.tags.filter((tag) => !isAutomaticTag(tag.name)));
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setIsFetchingTags(false);
    }
  };

  const activeTagIds = (): string[] =>
    tagRecords.filter((r) => r.isActive).map((r) => r.tag.id as string);

  const run = async (fn: () => Promise<void>) => {
    setLocalSubmitting(true);
    try {
      await fn();
    } catch (error) {
      // onAssignTags reports the failure itself; stopping here just skips the
      // success message and leaves the modal open for a retry.
      Sentry.captureException(error);
    } finally {
      setLocalSubmitting(false);
    }
  };

  // --- Add ---
  const selectedTagName = addTagId
    ? availableTags.find((t) => t.id === addTagId)?.name
    : undefined;
  const addIsManual = selectedTagName ? MANUAL_TAG_NAMES.includes(selectedTagName) : false;
  const addIsClosed = selectedTagName === "Closed";
  const deleteIsClosed = deletingRecord?.tag.name === "Closed";
  const addOkDisabled =
    !addTagId ||
    (addIsManual && supportsEffectiveDate && !addEffectiveDate) ||
    (addIsClosed && !!companyId && addProofUrls.length === 0);
  const deleteOkDisabled =
    deleteIsClosed && !!companyId && reopenProofUrls.length === 0;

  const handleAddConfirm = () =>
    run(async () => {
      if (!addTagId) return;
      const newIds = [...activeTagIds(), addTagId];
      await onAssignTags(newIds, {
        effectiveDate:
          addIsManual && supportsEffectiveDate && addEffectiveDate
            ? addEffectiveDate.toISOString()
            : undefined,
        notes: addNotes.trim() || undefined,
        proofOfClosureDocumentUrls:
          addIsClosed && addProofUrls.length > 0 ? addProofUrls : undefined,
      });
      message.success("Tag added");
      setShowAddModal(false);
      setAddTagId(undefined);
      setAddEffectiveDate(null);
      setAddNotes("");
      setAddProofUrls([]);
    });

  const handleAddCancel = () => {
    setShowAddModal(false);
    setAddTagId(undefined);
    setAddEffectiveDate(null);
    setAddNotes("");
    setAddProofUrls([]);
  };

  // --- Edit effective date ---
  const handleEditOpen = (record: TagRecord) => {
    setEditingRecord(record);
    setEditEffectiveDate(
      record.activatedAt ? moment(record.activatedAt) : null,
    );
    setShowEditModal(true);
  };

  const handleEditConfirm = () =>
    run(async () => {
      if (!editingRecord) return;
      await onAssignTags([editingRecord.tag.id as string], {
        effectiveDate: editEffectiveDate
          ? editEffectiveDate.toISOString()
          : undefined,
      });
      message.success("Effective date updated");
      setShowEditModal(false);
      setEditingRecord(null);
      setEditEffectiveDate(null);
    });

  const handleEditCancel = () => {
    setShowEditModal(false);
    setEditingRecord(null);
    setEditEffectiveDate(null);
  };

  // --- Delete ---
  const handleDeleteOpen = (record: TagRecord) => {
    setDeletingRecord(record);
    setDeleteEffectiveDate(record.tag.name === "Closed" ? moment() : null);
    setReopenProofUrls([]);
    setDeleteComment("");
    setUploadSession((session) => session + 1);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () =>
    run(async () => {
      if (!deletingRecord) return;
      const remaining = activeTagIds().filter(
        (id) => id !== (deletingRecord.tag.id as string),
      );
      await onAssignTags(remaining, {
        effectiveDate: deleteEffectiveDate
          ? deleteEffectiveDate.toISOString()
          : undefined,
        notes: deleteIsClosed ? deleteComment.trim() || undefined : undefined,
        proofOfReopeningDocumentUrls:
          deleteIsClosed && reopenProofUrls.length > 0
            ? reopenProofUrls
            : undefined,
      });
      message.success("Tag removed");
      setShowDeleteModal(false);
      setDeletingRecord(null);
      setDeleteEffectiveDate(null);
      setReopenProofUrls([]);
      setDeleteComment("");
    });

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeletingRecord(null);
    setDeleteEffectiveDate(null);
    setReopenProofUrls([]);
    setDeleteComment("");
  };

  const assignedTagIds = new Set(activeTagIds());
  const unassignedTags = availableTags.filter((t) => !assignedTagIds.has(t.id as string));

  const dateLabel = supportsEffectiveDate ? "Effective Date" : "Date Added";

  const columns: ColumnsType<TagRecord> = [
    {
      title: "Tag",
      key: "name",
      render: (_: unknown, record: TagRecord) => (
        <Tag
          color={tagColors[record.tag.name] || "default"}
          style={{ fontSize: 12, padding: "2px 8px" }}
        >
          {record.tag.name}
        </Tag>
      ),
    },
    {
      title: dateLabel,
      key: "date",
      render: (_: unknown, record: TagRecord) => {
        const val = supportsEffectiveDate ? record.activatedAt : record.createdAt;
        return val ? (
          moment(val).format("DD MMM YYYY, HH:mm")
        ) : (
          <span style={{ color: "#bfbfbf" }}>—</span>
        );
      },
    },
    {
      title: "Created",
      key: "createdAt",
      render: (_: unknown, record: TagRecord) =>
        record.createdAt ? (
          moment(record.createdAt).format("DD MMM YYYY, HH:mm")
        ) : (
          <span style={{ color: "#bfbfbf" }}>—</span>
        ),
    },
    {
      title: "Last Updated",
      key: "updatedAt",
      render: (_: unknown, record: TagRecord) =>
        record.updatedAt ? (
          moment(record.updatedAt).format("DD MMM YYYY, HH:mm")
        ) : (
          <span style={{ color: "#bfbfbf" }}>—</span>
        ),
    },
    {
      title: "Status",
      key: "isActive",
      render: (_: unknown, record: TagRecord) =>
        record.isActive ? (
          <Badge status="success" text="Active" />
        ) : (
          <Badge status="default" text="Inactive" />
        ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: unknown, record: TagRecord) => {
        const isManual = MANUAL_TAG_NAMES.includes(record.tag.name);
        if (!record.isActive) return null;
        return (
          <Space size="middle">
            {supportsEffectiveDate && isManual && (
              <Tooltip title="Update effective date">
                <Button
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleEditOpen(record)}
                  style={{ padding: 0 }}
                >
                  Edit date
                </Button>
              </Tooltip>
            )}
            <Tooltip title="Remove tag">
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteOpen(record)}
                style={{ padding: 0 }}
              >
                Remove
              </Button>
            </Tooltip>
          </Space>
        );
      },
    },
  ];
  const displayColumns = isReadOnly
    ? columns.filter((column) => column.key !== "actions")
    : columns;

  return (
    <Spin spinning={submitting}>
      {!isReadOnly && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <Button
            className="base-button"
            size="middle"
            icon={<PlusOutlined />}
            onClick={() => {
              setUploadSession((session) => session + 1);
              setShowAddModal(true);
            }}
            loading={isFetchingTags}
          >
            Add Tag
          </Button>
        </div>
      )}

      <Table<TagRecord>
        dataSource={tagRecords}
        columns={displayColumns}
        rowKey="id"
        size="small"
        pagination={false}
        locale={{ emptyText: "No tags assigned" }}
        rowClassName={(r) => (!r.isActive ? "ant-table-row-disabled" : "")}
      />

      {/* Add Tag */}
      {!isReadOnly &&
        ModalCreator({
          title: "Add Tag",
          okText: "Add",
          okType: "primary",
          okayButtonProps: { disabled: addOkDisabled },
          showModal: showAddModal,
          onOkay: handleAddConfirm,
          toggleModal: handleAddCancel,
          isLoading: localSubmitting,
          inscription: (
            <div>
              <div style={{ marginBottom: 12 }}>
                <label
                  style={{ display: "block", marginBottom: 6, fontWeight: 500 }}
                >
                  Tag
                </label>
                <Select
                  style={{ width: "100%" }}
                  placeholder="Select a tag"
                  value={addTagId}
                  onChange={(val) => {
                    setAddTagId(val);
                    setAddEffectiveDate(null);
                    setAddNotes("");
                    setAddProofUrls([]);
                  }}
                  optionFilterProp="children"
                  showSearch
                  loading={isFetchingTags}
                >
                  {unassignedTags.map((tag) => (
                    <Option key={tag.id as string} value={tag.id as string}>
                      {tag.name}
                    </Option>
                  ))}
                </Select>
              </div>
              {supportsEffectiveDate && addIsManual && (
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 6,
                      fontWeight: 500,
                    }}
                  >
                    Effective Date <span style={{ color: "#ff4d4f" }}>*</span>
                  </label>
                  <DatePicker
                    style={{ width: "100%" }}
                    value={addEffectiveDate}
                    disabledDate={(d) => d && d.isAfter(moment())}
                    onChange={(date) => setAddEffectiveDate(date)}
                  />
                </div>
              )}
              {showAddModal && addIsClosed && companyId && (
                <div style={{ marginTop: 12 }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 6,
                      fontWeight: 500,
                    }}
                  >
                    Proof of Closure Document(s){" "}
                    <span style={{ color: "#ff4d4f" }}>*</span>
                  </label>
                  <FileUpload
                    key={`proof-of-closure-${uploadSession}`}
                    companyId={companyId}
                    multiple
                    onFilesChange={(files) =>
                      setAddProofUrls(files.map((file) => file.url))
                    }
                    accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                  />
                </div>
              )}
              {addIsManual && (
                <div style={{ marginTop: 12 }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 6,
                      fontWeight: 500,
                    }}
                  >
                    Notes (Optional)
                  </label>
                  <Input.TextArea
                    value={addNotes}
                    onChange={(event) => setAddNotes(event.target.value)}
                    placeholder="Add optional notes"
                    rows={3}
                  />
                </div>
              )}
            </div>
          ),
        })}

      {/* Edit Effective Date */}
      {!isReadOnly &&
        ModalCreator({
          title: "Update Effective Date",
          okText: "Save",
          okType: "primary",
          okayButtonProps: { disabled: !editEffectiveDate },
          showModal: showEditModal,
          onOkay: handleEditConfirm,
          toggleModal: handleEditCancel,
          isLoading: localSubmitting,
          inscription: (
            <div>
              <p style={{ marginBottom: 8 }}>
                Update the effective date for{" "}
                <strong>{editingRecord?.tag.name}</strong>:
              </p>
              <DatePicker
                style={{ width: "100%" }}
                value={editEffectiveDate}
                disabledDate={(d) => d && d.isAfter(moment())}
                onChange={(date) => setEditEffectiveDate(date)}
              />
            </div>
          ),
        })}

      {/* Remove Tag */}
      {!isReadOnly &&
        ModalCreator({
          title: "Remove Tag",
          okText: "Remove",
          okType: "danger",
          okayButtonProps: { disabled: deleteOkDisabled },
          showModal: showDeleteModal,
          onOkay: handleDeleteConfirm,
          toggleModal: handleDeleteCancel,
          isLoading: localSubmitting,
          inscription: (
            <div>
              <div style={{ marginBottom: 12 }}>
                Are you sure you want to remove the{" "}
                <strong>{deletingRecord?.tag.name}</strong> tag?
              </div>
              {deleteIsClosed && (
                <>
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: 6,
                        fontWeight: 500,
                      }}
                    >
                      Effective Date (Optional)
                    </label>
                    <DatePicker
                      style={{ width: "100%" }}
                      value={deleteEffectiveDate}
                      allowClear
                      disabledDate={(d) => d && d.isAfter(moment())}
                      onChange={(date) => setDeleteEffectiveDate(date)}
                    />
                  </div>
                  {showDeleteModal && companyId && (
                    <div style={{ marginTop: 12 }}>
                      <label
                        style={{
                          display: "block",
                          marginBottom: 6,
                          fontWeight: 500,
                        }}
                      >
                        Proof of Reopening Document(s){" "}
                        <span style={{ color: "#ff4d4f" }}>*</span>
                      </label>
                      <FileUpload
                        key={`proof-of-reopening-${uploadSession}`}
                        companyId={companyId}
                        multiple
                        onFilesChange={(files) =>
                          setReopenProofUrls(files.map((file) => file.url))
                        }
                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                      />
                    </div>
                  )}
                  <div style={{ marginTop: 12 }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: 6,
                        fontWeight: 500,
                      }}
                    >
                      Comment (Optional)
                    </label>
                    <Input.TextArea
                      value={deleteComment}
                      onChange={(event) => setDeleteComment(event.target.value)}
                      placeholder="Add an optional comment"
                      rows={3}
                    />
                  </div>
                </>
              )}
            </div>
          ),
        })}
    </Spin>
  );
};

export default TagsManagement;
